import { type OidcMetadata } from 'oidc-client-ts';
import { isCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';

const COMMISSIONING_LOGIN_PATH = '/commissioning/login';

/**
 * cognito-local exposes a stripped-down OpenID discovery document (issuer and JWKS only).
 * Authorization and token endpoints sit at the server root, so we supply them here for
 * oidc-client-ts to run signinRedirect against the local emulator.
 * @param authority 
 * @returns Partial<OidcMetadata> | undefined
 */
export function getCognitoLocalMetadataSeed(authority: string): Partial<OidcMetadata> | undefined {
  try {
    const authorityUrl = new URL(authority);
    const isCognitoLocal =
      authorityUrl.port === '8005' && ['localhost', '127.0.0.1'].includes(authorityUrl.hostname);

    if (!isCognitoLocal) {
      return undefined;
    }

    const issuer = authority.replace(/\/$/, '');
    const { origin } = authorityUrl;

    return {
      issuer,
      authorization_endpoint: `${origin}/oauth2/authorize`,
      token_endpoint: `${origin}/oauth2/token`,
    };
  } catch {
    return undefined;
  }
}

/**
 * Returns true if the Cognito domain is a local emulator.
 * @returns boolean
 */
export function isCognitoLocalEmulator(): boolean {
  if (!isCognitoEnabled) {
    return false;
  }
  return getCognitoLocalMetadataSeed(import.meta.env.VITE_IDP_COGNITO_DOMAIN as string) !== undefined;
}

/**
 * Full page load so login state and persisted store are reset cleanly (cognito-local).
 * @returns void
 */
export function redirectToCommissioningLoginFresh(): void {
  window.location.assign(`${window.location.origin}${COMMISSIONING_LOGIN_PATH}`);
}
